/**
 * <tier-selector>
 *
 * Switches between two subscription-tier PRODUCTS on the same product page,
 * with no reload:
 *  - Toggles a locally duplicated "Subscription Features" block (same-page,
 *    no fetch — both tiers' content already exists in the shared template).
 *  - Switches the representative image by simulating a click on the
 *    matching gallery thumbnail (uses Horizon's own interaction handling,
 *    no private API assumptions).
 *  - Fetches the target product's Subscription Widget + Buy Buttons markup
 *    via Shopify's Section Rendering API and swaps those two custom
 *    elements in place, letting the browser's Custom Elements lifecycle
 *    reinitialize them against the new product/variant.
 *
 * Required markup contract (see blocks/tier-selector.liquid):
 *  - Each pill: [data-tier-pill] with data-features-id, data-product-url,
 *    data-product-handle, data-media-index
 *  - Host element: data-widget-target, data-buybutton-target,
 *    data-gallery-target, data-section-id
 *  - Each Features block: rendered with an `id` equal to its Custom ID
 *    (standard Horizon Group block behavior), matching a pill's
 *    data-features-id
 */
class TierSelector extends HTMLElement {
  connectedCallback() {
    this.pills = Array.from(this.querySelectorAll('[data-tier-pill]'));
    if (!this.pills.length) return;

    this.widgetSelector = this.dataset.widgetTarget || 'recharge-subscription-widget';
    this.buyButtonSelector = this.dataset.buybuttonTarget || '.buy-buttons-block';
    this.gallerySelector = this.dataset.galleryTarget || 'media-gallery';
    this.sectionId = this.dataset.sectionId || '';
    this.announcer = this.querySelector('[data-tier-announcer]');

    // handle -> { widgetHTML, buyButtonHTML }
    this.cache = new Map();
    this.isSwitching = false;

    this.pills.forEach((pill) => {
      pill.addEventListener('click', () => this.handleClick(pill));
    });

    // Enforce the initial hidden state client-side too, in case a tier's
    // Features block was left visible in the editor by mistake.
    const initialPill = this.pills.find((p) => p.classList.contains('is-active')) || this.pills[0];
    this.toggleFeatures(initialPill.dataset.featuresId);
  }

  async handleClick(pill) {
    if (this.isSwitching || pill.classList.contains('is-active')) return;

    const previousPill = this.pills.find((p) => p.classList.contains('is-active'));
    this.isSwitching = true;

    this.setActivePill(pill);
    this.toggleFeatures(pill.dataset.featuresId);
    this.switchImage(pill.dataset.mediaIndex);
    this.announce(`${pill.textContent.trim()} selected`);

    const { productUrl, productHandle } = pill.dataset;

    if (!productUrl || !productHandle) {
      // No cross-product target configured for this tier (e.g. it points
      // at the current product) — nothing further to fetch.
      this.isSwitching = false;
      return;
    }

    pill.setAttribute('aria-busy', 'true');

    try {
      const markup = await this.getTierMarkup(productHandle, productUrl);
      this.updateConfigScripts(markup.configById);
      this.swapNode(this.widgetSelector, markup.widgetHTML);
      this.swapNode(this.buyButtonSelector, markup.buyButtonHTML);

      this.dispatchEvent(
        new CustomEvent('tier-selector:changed', {
          bubbles: true,
          detail: { handle: productHandle, url: productUrl },
        })
      );
    } catch (error) {
      console.error('[tier-selector] Failed to switch tier:', error);
      this.showError(pill);

      // Roll back to the previous tier's visible state so the page never
      // shows a features/image mismatch against a widget that failed to load.
      if (previousPill) {
        this.setActivePill(previousPill);
        this.toggleFeatures(previousPill.dataset.featuresId);
        this.switchImage(previousPill.dataset.mediaIndex);
      }
    } finally {
      pill.removeAttribute('aria-busy');
      this.isSwitching = false;
    }
  }

  async getTierMarkup(handle, url) {
    if (this.cache.has(handle)) {
      return this.cache.get(handle);
    }

    const separator = url.includes('?') ? '&' : '?';
    const fetchUrl = `${url}${separator}section_id=${this.sectionId}&_=${Date.now()}`;

    const response = await fetch(fetchUrl, {
      credentials: 'same-origin',
      cache: 'no-store',
    });

    if (!response.ok) {
      throw new Error(`Section fetch failed with status ${response.status}`);
    }

    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');

    const widgetNode = doc.querySelector(this.widgetSelector);
    const buyButtonNode = doc.querySelector(this.buyButtonSelector);
    const configNodes = Array.from(doc.querySelectorAll('script[id^="SubscriptionWidgetConfig__"]'));

    if (!widgetNode || !buyButtonNode) {
      throw new Error(
        `Expected nodes not found in fetched markup for "${handle}" (widget: ${!!widgetNode}, buyButton: ${!!buyButtonNode})`
      );
    }

    const result = {
      widgetHTML: widgetNode.outerHTML,
      buyButtonHTML: buyButtonNode.outerHTML,
      // id -> fresh JSON/text content, so we can update the *existing* config
      // scripts on the live page before the swapped-in widget reads them.
      configById: Object.fromEntries(configNodes.map((node) => [node.id, node.textContent])),
    };

    this.cache.set(handle, result);
    return result;
  }

  updateConfigScripts(configById) {
    Object.entries(configById).forEach(([id, content]) => {
      const old = document.getElementById(id);
      if (!old) {
        console.warn(`[tier-selector] Config script #${id} not found on current page — skipping`);
        return;
      }

      // Mutating .textContent on an already-parsed inline <script> does NOT
      // re-run it — browsers only execute inline scripts once, at parse
      // time. Creating and inserting a fresh <script> element does execute,
      // which is what re-runs the IIFE and overwrites
      // window.SubscriptionWidgetConfig.{shop,formattedPrices,shopifyPlans}
      // with this tier's correctly currency-formatted values.
      const fresh = document.createElement('script');
      fresh.id = id;
      fresh.textContent = content;
      old.replaceWith(fresh);
    });
  }

  swapNode(selector, html) {
    const current = document.querySelector(selector);
    if (!current) {
      console.warn(`[tier-selector] No current element found for selector "${selector}"`);
      return;
    }

    const template = document.createElement('template');
    template.innerHTML = html.trim();
    const replacement = template.content.firstElementChild;

    if (replacement) {
      current.replaceWith(replacement);
    }
  }

  setActivePill(pill) {
    this.pills.forEach((p) => {
      const active = p === pill;
      p.classList.toggle('is-active', active);
      p.setAttribute('aria-pressed', String(active));
    });
  }

  toggleFeatures(activeId) {
    this.pills.forEach((p) => {
      const id = p.dataset.featuresId;
      if (!id) return;
      const el = document.getElementById(id);
      if (!el) return;

      const isActive = id === activeId;
      el.hidden = !isActive;
      el.style.display = isActive ? '' : 'none';
    });
  }

  switchImage(mediaIndexAttr) {
    let index = parseInt(mediaIndexAttr, 10);
    // -1 / 0 / NaN means "this tier's representative image is the product's
    // default featured image" — which is slide 1, not "leave the gallery
    // wherever a previous tier left it."
    if (!index || index < 1) index = 1;

    const gallery = document.querySelector(this.gallerySelector);
    if (!gallery) return;

    const thumbnails = gallery.querySelectorAll('.slideshow-controls__thumbnail');
    const target = thumbnails[index - 1];
    if (target) target.click();
  }

  announce(message) {
    if (this.announcer) this.announcer.textContent = message;
  }

  showError(pill) {
    pill.classList.add('tier-selector__pill--error');
    setTimeout(() => pill.classList.remove('tier-selector__pill--error'), 2000);
  }
}

if (!customElements.get('tier-selector')) {
  customElements.define('tier-selector', TierSelector);
}